import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { SITE } from "../data/site";

const LINKS = [
    { to: "/", label: "Home", id: "home" },
    { to: "/programs", label: "Programs", id: "programs" },
    { to: "/about", label: "About", id: "about" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                data-testid="nav-bar"
                className="fixed inset-x-0 top-0 z-50 border-b border-ink/5 bg-paper/85 backdrop-blur-md"
            >
                <div className="wrap flex h-[72px] items-center justify-between sm:h-24">
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        aria-label="Busy Bees Early Learning Academy — home"
                    >
                        <Logo testId="nav-official-logo" />
                    </Link>
                    <nav className="hidden items-center gap-1 md:flex">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                data-testid={`nav-link-${l.id}`}
                                className={({ isActive }) =>
                                    `rounded-full px-4 py-2 font-display text-[15px] font-medium transition-colors ${
                                        isActive
                                            ? "bg-honeysoft text-ink"
                                            : "text-ink-muted hover:bg-honeysoft/60 hover:text-ink"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <a
                            href={SITE.phoneHref}
                            data-testid="book-tour-btn"
                            className="btn-honey hidden !px-5 !py-2.5 !text-sm md:inline-flex"
                        >
                            <Phone className="h-4 w-4" /> Book a Tour
                        </a>
                        <button
                            data-testid="mobile-menu-btn"
                            onClick={() => setOpen((v) => !v)}
                            className="rounded-full border border-ink/10 bg-white/70 p-2.5 md:hidden"
                            aria-label={open ? "Close menu" : "Open menu"}
                        >
                            {open ? (
                                <X className="h-5 w-5" />
                            ) : (
                                <Menu className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                </div>
            </header>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-paper px-6 pt-28 md:hidden"
                    >
                        <nav className="flex flex-col gap-2">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.id}
                                    initial={{ opacity: 0, x: -18 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i + 0.05 }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`mobile-nav-link-${l.id}`}
                                        className={({ isActive }) =>
                                            `block rounded-2xl px-4 py-4 font-display text-3xl font-semibold ${
                                                isActive
                                                    ? "bg-honeysoft text-ink"
                                                    : "text-ink/75"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <a
                            href={SITE.phoneHref}
                            data-testid="mobile-book-tour-btn"
                            className="btn-honey mt-8 w-full"
                        >
                            <Phone className="h-4 w-4" /> Book a Tour ·{" "}
                            {SITE.phone}
                        </a>
                        <p className="mt-6 text-sm text-ink-soft">
                            {SITE.address}
                            <br />
                            {SITE.hoursLine}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
